import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, List, ListItem, ListItemText, IconButton, Paper, Snackbar, Collapse } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
export const LocationManager = () => {
    const [locations, setLocations] = useState([]);
    const [newTitle, setNewTitle] = useState('');
    const [newLatitude, setNewLatitude] = useState('');
    const [newLongitude, setNewLongitude] = useState('');
    const [bOpenSnack, setOpenSnack] = useState(false);
    const [snackMessage, setSnackMessage] = useState('');
    const [showMap, setShowMap] = useState(false);
    useEffect(() => {
        const stored = localStorage.getItem('com.boushar.locations');
        if (stored) {
            try {
                setLocations(JSON.parse(stored));
            }
            catch (e) {
                console.error('Failed to parse locations', e);
            }
        }
    }, []);
    const saveLocations = (newLocs) => {
        setLocations(newLocs);
        localStorage.setItem('com.boushar.locations', JSON.stringify(newLocs));
    };
    const captureLocation = () => {
        if (!navigator.geolocation) {
            setSnackMessage('Geolocation is not supported by your browser');
            setOpenSnack(true);
            return;
        }
        navigator.geolocation.getCurrentPosition((position) => {
            setNewLatitude(position.coords.latitude.toString());
            setNewLongitude(position.coords.longitude.toString());
        }, (error) => {
            setSnackMessage(`Error capturing location: ${error.message}`);
            setOpenSnack(true);
        });
    };
    const handleAddLocation = () => {
        if (!newTitle.trim()) {
            setSnackMessage('Please enter a location name.');
            setOpenSnack(true);
            return;
        }
        if (!newLatitude || !newLongitude || isNaN(parseFloat(newLatitude)) || isNaN(parseFloat(newLongitude))) {
            setSnackMessage('Please provide valid latitude and longitude.');
            setOpenSnack(true);
            return;
        }
        const newLoc = {
            title: newTitle.trim(),
            latitude: parseFloat(newLatitude),
            longitude: parseFloat(newLongitude),
        };
        const updated = [...locations, newLoc];
        saveLocations(updated);
        setNewTitle('');
        setNewLatitude('');
        setNewLongitude('');
        setSnackMessage(`Location '${newLoc.title}' added successfully.`);
        setOpenSnack(true);
    };
    const handleDeleteLocation = (index) => {
        const updated = [...locations];
        updated.splice(index, 1);
        saveLocations(updated);
    };
    return (React.createElement(Box, { sx: { maxWidth: 600, margin: 'auto', mt: 4 } },
        React.createElement(Snackbar, { open: bOpenSnack, autoHideDuration: 4000, onClose: () => setOpenSnack(false), message: snackMessage, anchorOrigin: { vertical: 'bottom', horizontal: 'center' } }),
        React.createElement(Typography, { variant: "h5", gutterBottom: true }, "Manage Locations"),
        React.createElement(Paper, { elevation: 3, sx: { p: 3, mb: 4 } },
            React.createElement(Typography, { variant: "h6", gutterBottom: true }, "Add New Location"),
            React.createElement(Box, { sx: { display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 } },
                React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 } },
                    React.createElement(TextField, { label: "Location Name (e.g., Home)", value: newTitle, onChange: (e) => setNewTitle(e.target.value), fullWidth: true }),
                    React.createElement(Box, { sx: { display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' } },
                        React.createElement(TextField, { label: "Latitude", value: newLatitude, onChange: (e) => setNewLatitude(e.target.value), size: "small", sx: { flex: 1, minWidth: '100px' } }),
                        React.createElement(TextField, { label: "Longitude", value: newLongitude, onChange: (e) => setNewLongitude(e.target.value), size: "small", sx: { flex: 1, minWidth: '100px' } }),
                        React.createElement(Button, { variant: "outlined", startIcon: React.createElement(LocationOnIcon, null), onClick: captureLocation, size: "medium" }, "Current location")),
                    React.createElement(Button, { variant: "contained", color: "primary", onClick: handleAddLocation }, "Save Location"),
                    React.createElement(Box, { sx: { display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mt: 1 } },
                        React.createElement(Button, { onClick: () => setShowMap(!showMap), endIcon: showMap ? React.createElement(ExpandLessIcon, null) : React.createElement(ExpandMoreIcon, null), color: "inherit" }, showMap ? 'Hide Map' : 'Show Map'))),
                React.createElement(Box, { sx: { flex: 1 } },
                    React.createElement(Box, { sx: { display: { xs: 'none', md: 'block' }, width: '100%', height: '100%', minHeight: 250 } },
                        React.createElement("iframe", { title: "location-map-desktop", width: "100%", height: "100%", frameBorder: "0", scrolling: "no", marginHeight: 0, marginWidth: 0, src: `https://maps.google.com/maps?q=${newLatitude || 0},${newLongitude || 0}&z=15&output=embed`, style: { borderRadius: '8px', border: '1px solid #ccc', minHeight: 250 } })),
                    React.createElement(Box, { sx: { display: { xs: 'block', md: 'none' } } },
                        React.createElement(Collapse, { in: showMap },
                            React.createElement(Box, { sx: { width: '100%', height: 250, mt: { xs: 0, md: 2 } } },
                                React.createElement("iframe", { title: "location-map-mobile", width: "100%", height: "100%", frameBorder: "0", scrolling: "no", marginHeight: 0, marginWidth: 0, src: `https://maps.google.com/maps?q=${newLatitude || 0},${newLongitude || 0}&z=15&output=embed`, style: { borderRadius: '8px', border: '1px solid #ccc' } }))))))),
        React.createElement(Typography, { variant: "h6" }, "Saved Locations"),
        locations.length === 0 ? (React.createElement(Typography, { color: "textSecondary" }, "No locations saved yet.")) : (React.createElement(List, null, locations.map((loc, idx) => (React.createElement(ListItem, { key: idx, secondaryAction: React.createElement(Box, null,
                React.createElement(IconButton, { "aria-label": "map", title: "Open in Google Maps", onClick: () => window.open(`https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`, '_blank'), sx: { mr: 1 } },
                    React.createElement(MapIcon, null)),
                React.createElement(IconButton, { edge: "end", "aria-label": "delete", onClick: () => handleDeleteLocation(idx) },
                    React.createElement(DeleteIcon, null))) },
            React.createElement(ListItemText, { primary: loc.title, secondary: `Lat: ${loc.latitude.toFixed(4)}, Lng: ${loc.longitude.toFixed(4)}` }))))))));
};
